package com.arena_web.ui.layouts

import androidx.compose.runtime.*
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.widgets.BackToTopButton
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.arena_web.ui.sections.header.AppHeader
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.SportColors
import com.arena_web.ui.utils.*
import com.arena_web.utils.AppSettings
import com.arena_web.utils.PageSeo
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier

import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*

/**
 * Shared page layout for all app pages (Arena, Profile, Chat, Events).
 * Static header (125px total: 30px top padding + 63px header + 32px gap),
 * dynamic content fills remaining space.
 */
@Composable
fun AppPageLayout(
    activeTab: SidebarTab? = null,
    // SEO: sets the document title ("Arena Pro Play - <title>") and per-page
    // meta description — these land in the static export, so public pages
    // should always pass them.
    title: String? = null,
    description: String = "",
    onSportChanged: (() -> Unit)? = null,
    backgroundImage: String? = null,
    mobileReady: Boolean = false,
    // Mobile-only: when set, replaces the sport-selector pill in the top bar
    // with the page title (Figma 8285:172272).
    mobilePageTitle: String? = null,
    // Mobile-only: chooses the right-side header action button (bell vs
    // heart) so per-page chrome can swap notifications for likes.
    mobileHeaderAction: com.arena_web.ui.sections.header.MobileHeaderAction =
        com.arena_web.ui.sections.header.MobileHeaderAction.Notification,
    // Mobile-only: trailing top-bar slot (avatar on Profile, chat shortcut on
    // Arena, empty elsewhere).
    mobileHeaderTrailing: com.arena_web.ui.sections.header.MobileHeaderTrailing =
        com.arena_web.ui.sections.header.MobileHeaderTrailing.None,
    // Mobile-only: back arrow instead of the brand logo in the top bar.
    mobileHeaderBack: Boolean = false,
    // Mobile-only: custom widget replacing the right-side action button.
    mobileHeaderActionSlot: (@Composable () -> Unit)? = null,
    // Mobile-only: show the light/dark theme button in the top bar (News).
    mobileShowThemeButton: Boolean = false,
    // Desktop-only: extra widget mounted in the AppHeader's right action
    // group (between the sport pill and the standard buttons).
    desktopHeaderExtras: (@Composable () -> Unit)? = null,
    // Mobile-only: hide the bottom nav for full-screen subviews.
    mobileHideBottomNav: Boolean = false,
    // Mobile-only: hide the global top bar for full-screen subviews.
    mobileHideTopBar: Boolean = false,
    // Mobile-only: drop the 16-px horizontal safe-area gutter so child content
    // can paint edge-to-edge (e.g. a full-bleed hero banner). The child then
    // owns its own horizontal padding where it needs it.
    mobileEdgeToEdge: Boolean = false,
    content: @Composable ColumnScope.() -> Unit
) {
    if (title != null) {
        PageSeo(title = "Arena Pro Play - $title", description = description)
    }

    // Ensure role is synced when entering player pages
    LaunchedEffect(Unit) {
        Storage.save(StorageKeys.ACTIVE_ROLE, "PLAYER")
    }

    // Reactive sport — reads from localStorage, updates when header changes sport
    var currentSport by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all")
    }
    val effectiveSport = AppSettings.globalPalette ?: currentSport

    val mobile = isMobile()

    CompositionLocalProvider(LocalSportColors provides SportColors.forSport(effectiveSport)) {
        if (mobile) {
            MobilePageLayout(
                activeTab = activeTab,
                onSportChanged = {
                    currentSport = (Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all")
                    onSportChanged?.invoke()
                },
                mobileReady = mobileReady,
                pageTitle = mobilePageTitle,
                headerAction = mobileHeaderAction,
                headerTrailing = mobileHeaderTrailing,
                headerBack = mobileHeaderBack,
                headerActionSlot = mobileHeaderActionSlot,
                showThemeButton = mobileShowThemeButton,
                hideBottomNav = mobileHideBottomNav,
                hideTopBar = mobileHideTopBar,
                edgeToEdge = mobileEdgeToEdge,
                content = content
            )
        } else {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(100.vh)
                    .overflow(Overflow.Hidden)
                    .styleModifier { property("position", "relative") }
            ) {
                if (backgroundImage != null) {
                    // Backdrop photo on its own layer so the light theme can
                    // relight it: invert + hue-rotate flips the dark
                    // surroundings to light while keeping the stadium's hues
                    // (green pitch stays green). Dark resolves `none` and
                    // renders the photo exactly as before.
                    org.jetbrains.compose.web.dom.Div(attrs = {
                        style {
                            property("position", "absolute")
                            property("inset", "0")
                            property("z-index", "0")
                            property("pointer-events", "none")
                            property("background-image", "url('$backgroundImage')")
                            property("background-size", "cover")
                            property("background-position", "center")
                            property("background-repeat", "no-repeat")
                            property("filter", "var(--l-hero-filter, none)")
                            // Light: multiply melts the relit photo's white
                            // surroundings into the page aurora (news-style
                            // backdrop) — only the stadium content remains.
                            property("mix-blend-mode", "var(--l-hero-blend, normal)")
                        }
                    })
                }
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                ) {
                    // position+z-index keep the header above full-bleed page
                    // content that extends up under the bar (e.g. the Arena
                    // map layer) — without it the later-in-DOM positioned
                    // content paints over the header controls.
                    Box(
                        modifier = Modifier.fillMaxWidth().flexShrink(0).sPadding(30, 40).sPaddingBottom(0)
                            .styleModifier {
                                property("position", "relative")
                                property("z-index", "2")
                            }
                    ) {
                        AppHeader(
                            activeTab = activeTab,
                            onSportChanged = { sportName ->
                                currentSport = sportName.lowercase()
                                onSportChanged?.invoke()
                            },
                            extraActions = desktopHeaderExtras
                        )
                    }

                    // Dynamic content — fills remaining height
                    Column(
                        modifier = Modifier.fillMaxWidth().flexGrow(1)
                            .minHeight(0.px)
                    ) {
                        content()
                    }
                }
            }
        }
    }
}

@Composable
fun PageLayout(
    title: String = "Arena Pro Play",
    // Full <title> override — used by the home page to serve a keyword-rich
    // SEO title instead of the "Arena Pro Play - <page>" tab pattern.
    seoTitle: String? = null,
    description: String = "",
    ogImage: String = "/og-image.png",
    // Opt-in: phones get the app-style MobilePageLayout (top bar + bottom
    // nav) instead of the scaled-down marketing desktop layout. Pages that
    // don't opt in keep their previous mobile rendering.
    mobileReady: Boolean = false,
    content: @Composable ColumnScope.() -> Unit
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    PageSeo(
        title = seoTitle ?: "Arena Pro Play - $title",
        description = description,
        ogImage = ogImage
    )

    if (mobileReady && isMobile()) {
        val sport = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
        CompositionLocalProvider(
            LocalSportColors provides SportColors.forSport(AppSettings.globalPalette ?: sport)
        ) {
            MobilePageLayout(mobileReady = true, content = content)
        }
        return
    }

    Box(
        Modifier
            .fillMaxWidth()
            .height(100.vh)
            .overflow(Overflow.Hidden)
            .styleModifier {
                // Dark: flat var(--l-s3, #02040B) (identical to the old colors.background).
                // Light: the aurora backdrop rides in via the light-only
                // --l-page-bg-image var (dark resolves the `none` fallback).
                property("background-color", "var(--arena-bg)")
                property("background-image", "var(--l-page-bg-image, none)")
                property("background-repeat", "var(--arena-bg-repeat)")
            }
    ) {
        Column(
            Modifier
                .fillMaxSize()
                .sPadding(30, 40)
                .sPaddingBottom(0),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Shared header on all pages — fixed 63px height, do not let flex shrink it.
            // Positioned above the home page's fixed ambient-orb layer (z 0) and the
            // content column (z 1) so the glow never washes over header controls.
            Box(
                modifier = Modifier.fillMaxWidth().flexShrink(0)
                    .styleModifier {
                        property("position", "relative")
                        property("z-index", "2")
                    }
            ) {
                AppHeader()
            }

            Column(
                HiddenScrollbarStyle.toModifier()
                    .fillMaxWidth().flexGrow(1).minHeight(0.px)
                    .styleModifier { property("overflow-y", "auto") },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                content()
            }

            BackToTopButton()
        }
    }
}
